import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react'

export type LayoutValueContextType<Properties extends string, T> = {
    values?: Record<Properties, T[]>
    selectedValue?: Partial<Record<Properties, T>>
    selectValue: (key: Properties, newValue: T) => void
    updateValues: (key: Properties, newValues: T[]) => void
}

type ContextProps<Properties extends string, T> = {
    defaultValues: Record<Properties, T[]>
    defaultSelected?: Partial<Record<Properties, T>>
    children: ReactNode
}

const Context = createContext<LayoutValueContextType<keyof {}, unknown> | undefined>({
    values: undefined,
    selectedValue: undefined,
    selectValue: () => {
        throw new Error('Method not implemented yet')
    },
    updateValues: () => {
        throw new Error('Method not implemented yet')
    },
})

const LayoutValueContext = <Properties extends string, T>({
    children,
    defaultValues,
    defaultSelected,
}: ContextProps<Properties, T>) => {
    const [values, setValues] = React.useState(defaultValues)
    const [selectedValue, setSelectedValue] = useState(defaultSelected ?? {})

    const selectValue = useCallback((key: Properties, newValue: unknown) => {
        setSelectedValue(prevState => ({ ...prevState, [key]: newValue }))
    }, [])

    const updateValues = useCallback((key: Properties, newValues: unknown[]) => {
        setValues(prevState => ({ ...prevState, [key]: newValues }))
    }, [])

    return (
        <Context.Provider
            value={{
                values,
                selectedValue,
                selectValue,
                updateValues,
            }}
        >
            {children}
        </Context.Provider>
    )
}

export const useLayoutValueContext = <Properties extends string, T>() =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useContext<LayoutValueContextType<Properties, T>>(Context)

export default LayoutValueContext
